<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    title="Operations overview"
    subtitle="Live metrics across Shopee and forwarding."
  >
    <div actions class="hidden md:flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-border bg-surface text-xs">
      <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
      <span class="text-muted-foreground">Updated&nbsp;</span>
      <span class="font-medium">{{ formatTimestamp(lastUpdated) }}</span>
    </div>
    <button actions hlmBtn variant="outline" size="sm" class="gap-1.5" (click)="refreshData()" [disabled]="isLoading">
      <ng-icon hlm name="lucideRefreshCw" class="size-4" [class.animate-spin]="isLoading" />
      {{ isLoading ? 'Refreshing…' : 'Refresh' }}
    </button>
  </app-page-header>

  <!-- KPI strip — 4 tiles, each with icon + value + label + hint -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

    <!-- Tile 1: Total Orders (admin) / My Active Workflows (non-admin) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">
            @if (isAdmin$ | async) { Total Orders } @else { My Active Workflows }
          </div>
          @if (metricsLoading) {
            <div class="h-7 w-20 mt-2.5 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
            <div class="h-3 w-24 mt-2 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">
              {{ (isAdmin$ | async) ? (shopeeMetrics.totalOrders | number) : (roleBasedMetrics.myActiveWorkflows | number) }}
            </div>
            <div class="flex items-center gap-1 mt-1.5">
              <span class="text-[11px] text-muted-foreground">All time</span>
            </div>
          }
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucidePackage" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Tile 2: Active Pre-Alerts (admin) / Same Role Online (non-admin) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">
            @if (isAdmin$ | async) { Active Pre-Alerts } @else { Same Role Online }
          </div>
          @if (metricsLoading) {
            <div class="h-7 w-20 mt-2.5 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
            <div class="h-3 w-24 mt-2 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">
              {{ (isAdmin$ | async) ? (shopeeMetrics.activePreAlerts | number) : (roleBasedMetrics.sameRoleOnline | number) }}
            </div>
            <div class="flex items-center gap-1 mt-1.5">
              <span class="text-[11px] text-muted-foreground">
                @if (isAdmin$ | async) { Active now } @else { Online now }
              </span>
            </div>
          }
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideBell" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Tile 3: Pending Tasks (admin) / Completed Today (non-admin) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">
            @if (isAdmin$ | async) { Pending Tasks } @else { Completed Today }
          </div>
          @if (metricsLoading) {
            <div class="h-7 w-20 mt-2.5 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
            <div class="h-3 w-24 mt-2 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">
              {{ (isAdmin$ | async) ? (systemMetrics.pendingTasks | number) : (roleBasedMetrics.myCompletedToday | number) }}
            </div>
            <div class="flex items-center gap-1 mt-1.5">
              <span class="text-[11px] text-muted-foreground">Requiring attention</span>
            </div>
          }
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideCheck" class="size-4" />
        </span>
      </div>
    </div>

    <!-- Tile 4: System Health (admin) / Team Activity (non-admin) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="p-5 flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="text-xs font-medium text-muted-foreground">
            @if (isAdmin$ | async) { System Health } @else { Team Activity }
          </div>
          @if (metricsLoading) {
            <div class="h-7 w-20 mt-2.5 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
            <div class="h-3 w-24 mt-2 pulse-soft rounded-md" style="background-color: rgb(var(--ds-surface-overlay, 231 229 228))"></div>
          } @else {
            <div class="text-2xl font-semibold tabular-nums tracking-tight mt-1.5">
              {{ (isAdmin$ | async) ? (systemMetrics.systemHealth | number) + '%' : (roleBasedMetrics.teamActivity | number) + '%' }}
            </div>
            <div class="flex items-center gap-1 mt-1.5">
              <span class="text-[11px] text-muted-foreground">
                @if (isAdmin$ | async) { All services nominal } @else { Active this session }
              </span>
            </div>
          }
        </div>
        <span class="grid place-items-center w-9 h-9 rounded-md shrink-0"
              style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
          <ng-icon hlm name="lucideActivity" class="size-4" />
        </span>
      </div>
    </div>

  </div>

  <!-- Main content row: Recent Activity + right column -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

    <!-- Recent Activity (col-span-2) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm lg:col-span-2">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold leading-tight">Recent activity</h3>
          <p class="text-xs text-muted-foreground mt-0.5">Orders and pre-alert events</p>
        </div>
        <button hlmBtn variant="ghost" size="sm" class="gap-1" (click)="loadMoreActivity()" [disabled]="loadingMoreActivity">
          @if (loadingMoreActivity) {
            <ng-icon hlm name="lucideRefreshCw" class="size-3.5 animate-spin" />
          } @else {
            Load more <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
          }
        </button>
      </div>

      @if (activityLoading) {
        <div class="px-5 py-4">
          <app-skeleton [rows]="5" rowHeight="h-9" />
        </div>
      } @else if (recentActivity.length === 0) {
        <div class="py-10 text-center text-sm text-muted-foreground">No recent activity</div>
      } @else {
        <div class="px-5 py-3">
          <ol class="relative">
            @for (item of recentActivity; track item.id) {
              <li class="relative pl-8 py-2.5">
                <span class="absolute left-2 top-3 w-px bottom-0 bg-border" aria-hidden="true"></span>
                <span class="absolute left-0 top-2.5 grid place-items-center w-5 h-5 rounded-full bg-surface ring-1 ring-border"
                      style="color: rgb(var(--ds-mfg, 120 113 108))">
                  @if (item.type === 'order') {
                    <ng-icon hlm name="lucidePackage" class="size-3" />
                  } @else {
                    <ng-icon hlm name="lucideBell" class="size-3" />
                  }
                </span>
                <div class="flex items-baseline justify-between gap-3">
                  <p class="text-sm">
                    <span class="font-medium">{{ item.user }}</span>
                    <span class="text-muted-foreground"> — {{ item.description }}</span>
                  </p>
                  <span class="text-[11px] tabular-nums text-muted-foreground shrink-0">
                    {{ formatTimestamp(item.timestamp) }}
                  </span>
                </div>
              </li>
            }
          </ol>
        </div>
      }
    </div>

    <!-- Right column -->
    <div class="space-y-4">

      <!-- Pending Tasks card -->
      <div class="rounded-lg border border-border bg-surface shadow-sm">
        <div class="px-5 pt-4 pb-3 border-b border-border">
          <h3 class="text-base font-semibold leading-tight">Pending tasks</h3>
          <p class="text-xs text-muted-foreground mt-0.5">Role-based action items</p>
        </div>
        @if (tasksLoading) {
          <div class="p-5">
            <app-skeleton [rows]="3" rowHeight="h-8" />
          </div>
        } @else if (pendingTasks.length === 0) {
          <div class="p-5 text-sm text-muted-foreground">No pending tasks.</div>
        } @else {
          <div class="p-5 space-y-2">
            @for (task of pendingTasks; track task.id) {
              <button class="w-full flex items-center gap-3 px-3 h-9 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm text-left"
                      (click)="navigateToTask(task)">
                <span class="w-2 h-2 rounded-full shrink-0"
                      [style.background-color]="task.priority === 'high' ? 'rgb(239 68 68)' : task.priority === 'medium' ? 'rgb(245 158 11)' : 'rgb(107 114 128)'">
                </span>
                <span class="flex-1 min-w-0 truncate font-medium">{{ task.title }}</span>
                <ng-icon hlm name="lucideChevronRight" class="size-3.5 shrink-0 text-muted-foreground" />
              </button>
            }
          </div>
        }
      </div>

      <!-- Quick links card -->
      <div class="rounded-lg border border-border bg-surface shadow-sm">
        <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <div>
            <h3 class="text-base font-semibold leading-tight">Jump to</h3>
            <p class="text-xs text-muted-foreground mt-0.5">Modules for your role</p>
          </div>
          @if (currentUserRole) {
            <span class="inline-flex items-center gap-1 rounded-md px-2 h-5 text-[11px] font-medium border border-border-strong text-foreground/70">
              {{ currentUserRole }}
            </span>
          }
        </div>
        <div class="p-3 grid grid-cols-2 gap-2">

          <!-- Shopee Orders — shown for ShopeeDocsTeam + Admin -->
          @if (isShopeeDocsTeam$ | async) {
            <a routerLink="/dashboard/orders" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
              <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                    style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
                <ng-icon hlm name="lucidePackage" class="size-3.5" />
              </span>
              <span class="text-foreground/90 font-medium truncate">Orders</span>
            </a>
            <a routerLink="/dashboard/pre-alerts" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
              <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                    style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
                <ng-icon hlm name="lucideBell" class="size-3.5" />
              </span>
              <span class="text-foreground/90 font-medium truncate">Pre-Alerts</span>
            </a>
            <a routerLink="/dashboard/mobile-scanner" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
              <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                    style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
                <ng-icon hlm name="lucideScanLine" class="size-3.5" />
              </span>
              <span class="text-foreground/90 font-medium truncate">Scanner</span>
            </a>
          }

          <!-- Forwarding — shown for Admin -->
          @if (isAdmin$ | async) {
            <a routerLink="/dashboard/forwarding/quotations" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
              <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                    style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
                <ng-icon hlm name="lucideFileText" class="size-3.5" />
              </span>
              <span class="text-foreground/90 font-medium truncate">Quotations</span>
            </a>
            <a routerLink="/dashboard/user-management" class="flex items-center gap-2.5 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm">
              <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                    style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
                <ng-icon hlm name="lucideUsers" class="size-3.5" />
              </span>
              <span class="text-foreground/90 font-medium truncate">Users</span>
            </a>
          }

        </div>
      </div>

    </div>
  </div>

  <!-- Lower row: Shopee Workflow card + System snapshot -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

    <!-- Shopee Workflow card (col-span-2) -->
    <div class="rounded-lg border border-border bg-surface shadow-sm lg:col-span-2">
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold leading-tight">Shopee Operations</h3>
          <p class="text-xs text-muted-foreground mt-0.5">E-commerce order management workflow</p>
        </div>
        <button hlmBtn variant="ghost" size="sm" (click)="navigateToWorkflow('shopee')">
          Open <ng-icon hlm name="lucideChevronRight" class="size-3.5" />
        </button>
      </div>
      <div class="p-5">
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <div class="text-center">
            <div class="text-2xl font-semibold tabular-nums tracking-tight">{{ shopeeMetrics.totalOrders | number }}</div>
            <div class="text-xs text-muted-foreground mt-1">Total Orders</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold tabular-nums tracking-tight">{{ shopeeMetrics.activePreAlerts | number }}</div>
            <div class="text-xs text-muted-foreground mt-1">Pre-Alerts</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold tabular-nums tracking-tight">{{ shopeeMetrics.recentOrders | number }}</div>
            <div class="text-xs text-muted-foreground mt-1">Recent Orders</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-semibold tabular-nums tracking-tight">{{ shopeeMetrics.systemActivity | number }}</div>
            <div class="text-xs text-muted-foreground mt-1">Processing</div>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <a routerLink="/dashboard/orders">
            <button hlmBtn variant="outline" size="sm" class="gap-1.5">
              <ng-icon hlm name="lucidePackage" class="size-3.5" />
              Orders
            </button>
          </a>
          <a routerLink="/dashboard/pre-alerts">
            <button hlmBtn variant="outline" size="sm" class="gap-1.5">
              <ng-icon hlm name="lucideBell" class="size-3.5" />
              Pre-Alerts
            </button>
          </a>
          <a routerLink="/dashboard/mobile-scanner">
            <button hlmBtn variant="outline" size="sm" class="gap-1.5">
              <ng-icon hlm name="lucideScanLine" class="size-3.5" />
              Mobile Scanner
            </button>
          </a>
        </div>
      </div>
    </div>

    <!-- Quick Actions / System snapshot -->
    <div class="rounded-lg border border-border bg-surface shadow-sm">
      <div class="px-5 pt-4 pb-3 border-b border-border">
        <h3 class="text-base font-semibold leading-tight">Quick actions</h3>
        <p class="text-xs text-muted-foreground mt-0.5">Common operations</p>
      </div>
      <div class="p-4 space-y-2">
        <button class="w-full flex items-center gap-3 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm"
                (click)="refreshData()">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucideRefreshCw" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium">Refresh data</span>
        </button>

        @if (isAdmin$ | async) {
          <button class="w-full flex items-center gap-3 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm"
                  (click)="navigateToUserManagement()">
            <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                  style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
              <ng-icon hlm name="lucideUsers" class="size-3.5" />
            </span>
            <span class="text-foreground/90 font-medium">User management</span>
          </button>
        }

        <button class="w-full flex items-center gap-3 px-3 h-10 rounded-md border border-border bg-surface hover:bg-surface-raised hover:border-border-strong transition text-sm"
                (click)="viewSystemHealth()">
          <span class="grid place-items-center w-7 h-7 rounded-md shrink-0"
                style="background-color: rgb(var(--ds-accent-light, 239 246 255)); color: rgb(var(--ds-accent, 37 99 235))">
            <ng-icon hlm name="lucideActivity" class="size-3.5" />
          </span>
          <span class="text-foreground/90 font-medium">System health</span>
        </button>

      </div>
    </div>

  </div>

  <!-- Error / Success messages -->
  @if (errorMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <ng-icon hlm name="lucideActivity" class="size-4 mt-0.5 shrink-0" />
      <div class="flex-1">{{ errorMessage }}</div>
      <button class="shrink-0 text-red-500 hover:text-red-700" (click)="clearMessages()" aria-label="Dismiss">
        <ng-icon hlm name="lucideX" class="size-4" />
      </button>
    </div>
  }

  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
      <ng-icon hlm name="lucideCheck" class="size-4 mt-0.5 shrink-0" />
      <div class="flex-1">{{ successMessage }}</div>
      <button class="shrink-0 text-green-500 hover:text-green-700" (click)="clearMessages()" aria-label="Dismiss">
        <ng-icon hlm name="lucideX" class="size-4" />
      </button>
    </div>
  }

</div>
